// src/app/pages/cost-metrics/simulation-panel/simulation-overview/simulation-overview.ts
// Minimal updates: clamp slider to [2019, 2045], initialize to 2019→2045,
// and add a robust CVD patients lookup (handles CVD_Patients).

import { Component, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { KnobModule } from 'primeng/knob';
import { BadgeModule } from 'primeng/badge';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';

import { SimulationApiService } from '../../../core/shared/simulation-api.service';
import { AnalysisStore } from '../../../core/shared/analysis.store';
import { YearStore } from '../../../core/shared/year.store';

type HtnBlock = Record<string, Record<string, number>>;

@Component({
    selector: 'app-simulation-overview',
    standalone: true,
    templateUrl: './simulation-overview.html',
    imports: [FormsModule, ButtonModule, ChartModule, TabsModule, CheckboxModule, SelectModule, SliderModule, TooltipModule, AccordionModule, KnobModule, BadgeModule, ToastModule, InputTextModule, SelectModule, PopoverModule],
    providers: [MessageService]
})
export class SimulationOverviewComponent implements OnInit {
    @Output() chartReady = new EventEmitter<void>();

    private simApi = inject(SimulationApiService);
    private msg = inject(MessageService);
    private analysisStore = inject(AnalysisStore);
    private yearStore = inject(YearStore, { optional: true });

    // Global bounds
    private readonly MIN_YEAR = 2011;
    private readonly MAX_YEAR = 2045;

    isLoading = true;

    // Slider dataset bounds (clamped to [2019, 2045])
    sliderYearMin = this.MIN_YEAR;
    sliderYearMax = this.MAX_YEAR;

    // Selected (right thumb) and display min (left thumb)
    selectedYear = this.MAX_YEAR;
    minYearUI = 2019;

    // Two-way model for p-slider with [range]="true"
    rangeValues: number[] = [2019, this.MAX_YEAR];

    prevention = 0.05;
    screening = 0.05;
    treatment = 0.05;

    stats = {
        normalBP: 0,
        cvdPatients: 0,
        hypertensionTotal: 0,
        preHypertension: 0,
        totalPopulationAtRisk: 0
    };

    seriesColors = ['#aa1646', '#f193b2', '#e95887', '#f8c9d8'];
    chartData: any;
    chartOptionsSmallTooltipCenterTitle: any;

    private htn: HtnBlock | null = null;
    private years: string[] = [];

    ngOnInit(): void {
        this.simApi.loadInitialFromFile('assets/initial-response.json').subscribe({
            next: (resp: { years?: string[]; htn?: HtnBlock }) => {
                this.years = resp?.years ?? [];
                this.htn = resp?.htn ?? {};

                // Dataset bounds from response, clamped to [2019, 2045]
                const respMin = this.years.length ? Math.min(...this.years.map((y) => Number(y))) : this.MIN_YEAR;
                const respMax = this.years.length ? Math.max(...this.years.map((y) => Number(y))) : this.MAX_YEAR;
                this.sliderYearMin = Math.max(this.MIN_YEAR, respMin);
                this.sliderYearMax = Math.min(this.MAX_YEAR, respMax);

                // Initialize to [2019 → 2045] (or clamped by available data)
                this.minYearUI = 2019;
                this.selectedYear = this.sliderYearMax;
                this.rangeValues = [this.minYearUI, this.selectedYear];

                this.yearStore?.setDisplayMinYear?.(this.minYearUI);
                this.yearStore?.setYear?.(this.selectedYear);

                this.applyFromHtn(String(this.selectedYear));
                this.setupChartOptions();
                this.setupChartData();
                this.isLoading = false;
                this.chartReady.emit();

                // React to external year changes (clamped to slider bounds)
                this.yearStore?.year$?.subscribe((y: number) => {
                    const clamped = Math.min(this.sliderYearMax, Math.max(this.sliderYearMin, Number(y)));
                    if (this.selectedYear !== clamped) {
                        this.selectedYear = clamped;
                        if (this.minYearUI > this.selectedYear) this.minYearUI = this.selectedYear;
                        this.rangeValues = [this.minYearUI, this.selectedYear];
                        this.applyFromHtn(String(this.selectedYear));
                        this.setupChartData();
                    }
                });
            },
            error: (err: unknown) => {
                this.isLoading = false;
                this.setupChartOptions();
                this.setupChartData();
                console.error('Overview load error', err);
            }
        });
    }

    onYearSlide(event: any): void {
        if (event?.values || Array.isArray(this.rangeValues)) {
            const vals = event?.values ?? this.rangeValues;
            let left = Number(vals?.[0] ?? this.minYearUI);
            let right = Number(vals?.[1] ?? this.selectedYear);

            left = Math.min(this.sliderYearMax, Math.max(this.sliderYearMin, left));
            right = Math.min(this.sliderYearMax, Math.max(this.sliderYearMin, right));

            const min = Math.min(left, right);
            const max = Math.max(left, right);

            this.minYearUI = min;
            this.selectedYear = max;
            this.rangeValues = [this.minYearUI, this.selectedYear];
        } else {
            const value = typeof event === 'number' ? event : event?.value;
            const year = Math.min(this.sliderYearMax, Math.max(this.sliderYearMin, Number(value ?? this.selectedYear)));
            this.selectedYear = year;
            if (this.minYearUI > this.selectedYear) this.minYearUI = this.selectedYear;
            this.rangeValues = [this.minYearUI, this.selectedYear];
        }

        if (!this.htn) return;
        this.applyFromHtn(String(this.selectedYear));
        this.setupChartData();

        this.yearStore?.setYear?.(this.selectedYear);
        (this.analysisStore as any)?.selectedYear$?.next?.(this.selectedYear);
        this.yearStore?.setDisplayMinYear?.(this.minYearUI);
    }

    onYearChange(): void {
        if (!this.htn) return;

        const left = Math.min(this.sliderYearMax, Math.max(this.sliderYearMin, this.rangeValues?.[0] ?? this.minYearUI));
        const right = Math.min(this.sliderYearMax, Math.max(this.sliderYearMin, this.rangeValues?.[1] ?? this.selectedYear));
        this.minYearUI = Math.min(left, right);
        this.selectedYear = Math.max(left, right);
        this.rangeValues = [this.minYearUI, this.selectedYear];

        const y = String(this.selectedYear);
        this.applyFromHtn(y);
        this.setupChartData();

        this.yearStore?.setYear?.(this.selectedYear);
        (this.analysisStore as any)?.selectedYear$?.next?.(this.selectedYear);
        this.yearStore?.setDisplayMinYear?.(this.minYearUI);
    }

    private applyFromHtn(year: string): void {
        if (!this.htn) {
            this.stats = { normalBP: 0, cvdPatients: 0, hypertensionTotal: 0, preHypertension: 0, totalPopulationAtRisk: 0 };
            return;
        }

        const v = (series: string) => Number(String(this.htn?.[series]?.[year] ?? 0).replace(/,/g, ''));

        const normalBP = (v('Normal_BP_Male') || 0) + (v('Normal_BP_Female') || 0);

        // Robust CVD patients (handles both 'CVD_patients' and 'CVD_Patients')
        const cvdPatientsVal = ((): number => {
            const a = this.htn?.['CVD_patients']?.[year];
            const b = this.htn?.['CVD_Patients']?.[year];
            return Number(String(a ?? b ?? 0).replace(/,/g, '')) || 0;
        })();

        const preHyp = (v('Prehypertension_Male') || 0) + (v('Prehypertension_Female') || 0);
        const hypertension = (v('Diagnosed') || 0) + (v('Undiagnosed') || 0);

        const totalRisk = cvdPatientsVal + v('Prehypertension_Male') + v('Prehypertension_Female') + v('Normal_BP_Male') + v('Normal_BP_Female') + v('Prevalence_Female') + v('Prevalence_Male');

        this.stats = {
            normalBP: Math.round(normalBP),
            cvdPatients: Math.round(cvdPatientsVal),
            hypertensionTotal: Math.round(hypertension),
            preHypertension: Math.round(preHyp),
            totalPopulationAtRisk: Math.round(totalRisk)
        };
    }

    private setupChartData(): void {
        this.chartData = {
            labels: ['Normal BP', 'CVD Patients', 'Hypertension', 'Pre-hypertension'],
            datasets: [
                {
                    data: [this.stats.normalBP, this.stats.cvdPatients, this.stats.hypertensionTotal, this.stats.preHypertension],
                    backgroundColor: this.seriesColors,
                    hoverBackgroundColor: this.seriesColors,
                    borderWidth: 0
                }
            ]
        };
    }

    private setupChartOptions(): void {
        this.chartOptionsSmallTooltipCenterTitle = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: true,
                    intersect: true,
                    mode: 'nearest',
                    bodyFont: { size: 10 },
                    titleFont: { size: 10 },
                    padding: 6,
                    callbacks: {
                        label: (ctx: any) => {
                            const label = ctx.label || '';
                            const value = ctx.raw ?? 0;
                            return `${label}: ${this.formatNumber(value)}`;
                        }
                    }
                }
            },
            cutout: '80%',
            animation: { duration: 500, easing: 'easeOutQuart' }
        };
    }

    formatNumber(n: number): string {
        return n.toLocaleString('en-US');
    }
    formatMillions(n: number): string {
        return `${Math.round(n / 1_000_000)}M`;
    }
    get displayRange(): string {
        return this.minYearUI === this.selectedYear ? String(this.selectedYear) : `${this.minYearUI} to ${this.selectedYear}`;
    }
}
